import { useEffect, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import { BOOK_CAFE_FEATURES } from "./book-cafe-content";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import BookCafeManuals from "./BookCafeManuals";

interface PropsType {
  userState: userState;
}

interface BookCafePolicy {
  availabilityStatus?: "OPERATING" | "UNDER_REPAIR" | "OPENING_SOON";
  regularHourlyPrice?: number;
  hourlyPrice: number;
  maxContiguousHours?: number;
}

const BOOK_CAFE_PUBLIC_HOURS = "10:00 ~ 22:00";
const MANUALS_COACH_MARK_SEEN_KEY = "hpoint.book-cafe.manuals-coach-mark.seen";

const BOOK_CAFE_HERO_ARTWORKS = [
  "/images/hpoint-renewal/book-cafe/book-cafe-banner-cinema-kids-cutout-3d-v1.png",
  "/images/hpoint-renewal/book-cafe/book-cafe-banner-arcade-duel-cutout-3d-v1.png",
  "/images/hpoint-renewal/book-cafe/book-cafe-banner-yutnori-family-cutout-3d-v1.png",
] as const;

const mainApi = new HttpMainApi();

const BookCafeGuide = (props: PropsType) => {
  const navigate = useNavigate();
  const [policy, setPolicy] = useState<BookCafePolicy | null>(null);
  const [policyStatus, setPolicyStatus] = useState<"loading" | "ready" | "error">("loading");
  const [isManualsOpen, setIsManualsOpen] = useState(false);
  const [isManualsCoachMarkVisible, setIsManualsCoachMarkVisible] = useState(() => {
    try {
      return window.localStorage.getItem(MANUALS_COACH_MARK_SEEN_KEY) !== "true";
    } catch {
      return false;
    }
  });
  const [heroArtwork] = useState(
    () => BOOK_CAFE_HERO_ARTWORKS[Math.floor(Math.random() * BOOK_CAFE_HERO_ARTWORKS.length)],
  );

  useEffect(() => {
    if (isManualsCoachMarkVisible) {
      try {
        window.localStorage.setItem(MANUALS_COACH_MARK_SEEN_KEY, "true");
      } catch {
        // The manual remains usable if browser storage is unavailable.
      }
    }
  }, [isManualsCoachMarkVisible]);

  useEffect(() => {
    window.scrollTo(0, 0);
    let active = true;

    const loadPolicy = async () => {
      try {
        const response = await mainApi.get_book_cafe_policy({});
        if (!active) return;
        if (response.code === "200") {
          setPolicy(response.response as BookCafePolicy);
          setPolicyStatus("ready");
          return;
        }
        setPolicyStatus("error");
      } catch {
        if (active) setPolicyStatus("error");
      }
    };

    void loadPolicy();
    return () => {
      active = false;
    };
  }, []);

  const maxContiguousHours = policy?.maxContiguousHours ?? 5;
  const regularHourlyPrice = policy?.regularHourlyPrice ?? 10000;
  const discountRate = policy
    ? Math.round((1 - policy.hourlyPrice / regularHourlyPrice) * 100)
    : 0;

  const sendBookCafeClickLog = (eventType: string, path: string, menuName?: string) => {
    const payload: Record<string, string> = {
      eventType,
      userId: props.userState.userId,
      userName: props.userState.userName,
      userDong: props.userState.userDong,
      userHo: props.userState.userHo,
      path,
      source: "BOOK_CAFE_GUIDE",
    };
    if (menuName) payload.menuName = menuName;

    try {
      void mainApi.put_dashboard_event(payload).catch(() => undefined);
    } catch {
      // Logging must never block navigation.
    }
  };

  const moveToReservation = () => {
    sendBookCafeClickLog("BOOK_CAFE_MENU_CLICK", "/reservationbookcafe/reserve");
    navigate("/reservationbookcafe/reserve");
  };

  return (
    <main className="hpoint-renewal hp-reservation-page hp-book-cafe-guide">
      {isManualsCoachMarkVisible ? (
        <button className="hp-book-cafe-board-game-list__coach-mark-backdrop" type="button"
          aria-label="이용방법 안내 닫기" onClick={() => setIsManualsCoachMarkVisible(false)} />
      ) : null}
      <section
        className={`hp-book-cafe-guide__hero${isManualsCoachMarkVisible ? " hp-book-cafe-guide__hero--manual-highlight" : ""}`}
        aria-labelledby="book-cafe-guide-title"
      >
        <div className={`hp-book-cafe-guide__hero-copy${isManualsCoachMarkVisible ? " hp-book-cafe-guide__hero-copy--manual-highlight" : ""}`}>
          <h1 id="book-cafe-guide-title">단지 안에서 만나는 우리만의 파티룸</h1>
          <p>
            영화와 게임, 생일 모임과 휴식을 한 공간에서 즐기는 입주민 전용 프라이빗 멀티룸입니다.
          </p>
          <Button className={`hp-book-cafe-guide__manual-button${isManualsCoachMarkVisible ? " hp-book-cafe-guide__manual-button--spotlight" : ""}`} variant="outlined"
            aria-label="이용방법"
            aria-describedby={isManualsCoachMarkVisible ? "book-cafe-manuals-coach-mark" : undefined}
            startIcon={<MenuBookOutlinedIcon />} onClick={() => {
              setIsManualsCoachMarkVisible(false);
              setIsManualsOpen(true);
            }}>
            이용방법
            {isManualsCoachMarkVisible ? (
              <span id="book-cafe-manuals-coach-mark" role="tooltip"
                className="hp-book-cafe-board-game-list__coach-mark hp-book-cafe-guide__manual-coach-mark">
                시설 이용방법을 확인하세요
              </span>
            ) : null}
          </Button>
        </div>
        <div className="hp-book-cafe-guide__hero-artwork" aria-hidden="true">
          <img src={heroArtwork} alt="" />
        </div>
      </section>

      <section
        className="hp-book-cafe-guide__section hp-book-cafe-guide__experiences"
        aria-labelledby="book-cafe-experiences-title"
      >
        <div className="hp-book-cafe-guide__section-heading hp-book-cafe-guide__section-heading--split">
          <div>
            <h2 id="book-cafe-experiences-title">오늘은 어떻게 즐겨볼까요?</h2>
          </div>
          <p>카드를 선택하면 공간별 준비물과 이용 팁을 자세히 볼 수 있어요.</p>
        </div>

        <div className="hp-book-cafe-feature-list" aria-label="북카페 이용 장면">
          {BOOK_CAFE_FEATURES.map((feature) => (
            <Link
              className={`hp-book-cafe-feature-card hp-book-cafe-feature-card--${feature.accent}`}
              key={feature.slug}
              to={`/reservationbookcafe/guide/${feature.slug}`}
              onClick={() => sendBookCafeClickLog(
                "BOOK_CAFE_GUIDE_DETAIL_CLICK",
                `/reservationbookcafe/guide/${feature.slug}`,
                feature.title,
              )}
            >
              <div
                className={`hp-book-cafe-image-slot${feature.image ? " hp-book-cafe-image-slot--filled" : ""}`}
                aria-label={feature.image ? undefined : `${feature.title} 이미지 준비 중`}
              >
                {feature.image ? (
                  <img src={feature.image.src} alt={feature.image.alt} />
                ) : (
                  <>
                    <span>이미지 준비 중</span>
                    <small>{feature.eyebrow}</small>
                  </>
                )}
              </div>
              <div className="hp-book-cafe-feature-card__body">
                <span className="hp-book-cafe-feature-card__eyebrow">{feature.eyebrow}</span>
                <h3>{feature.title}</h3>
                <p>{feature.summary}</p>
                <ul aria-label={`${feature.title} 핵심 시설`}>
                  {feature.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
                </ul>
                <span className="hp-book-cafe-feature-card__more" aria-hidden="true">자세히 보기 <b>→</b></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="hp-book-cafe-guide__section" aria-labelledby="book-cafe-policy-title">
        <div className="hp-book-cafe-guide__section-heading">
          <div>
            <h2 id="book-cafe-policy-title">이용안내</h2>
          </div>
        </div>

        <div className="hp-book-cafe-policy-card">
          {policyStatus === "loading" ? (
            <div className="hp-book-cafe-policy-card__loading" aria-label="북카페 운영정보 불러오는 중">
              <CircularProgress size={22} />
              <span>운영정보를 불러오고 있어요.</span>
            </div>
          ) : null}
          {policyStatus === "error" ? (
            <p className="hp-book-cafe-policy-card__error" role="status">
              운영시간과 이용료를 불러오지 못했습니다. 예약 화면에서 다시 확인해 주세요.
            </p>
          ) : null}
          {policy ? (
            <dl className="hp-book-cafe-policy-card__facts">
              <div>
                <dt>운영시간</dt>
                <dd>
                  {BOOK_CAFE_PUBLIC_HOURS}
                </dd>
              </div>
              <div>
                <dt>이용료</dt>
                <dd className="hp-book-cafe-policy-card__price">
                  {discountRate > 0 ? (
                    <del
                      className="hp-book-cafe-policy-card__regular-price"
                      data-testid="book-cafe-regular-price"
                    >
                      1시간 {regularHourlyPrice.toLocaleString("ko-KR")}원
                    </del>
                  ) : (
                    <span>1시간 {regularHourlyPrice.toLocaleString("ko-KR")}원</span>
                  )}
                  {discountRate > 0 ? (
                    <>
                      <span className="hp-book-cafe-policy-card__discount-badge">
                        한시적 {discountRate}% 할인
                      </span>
                      <span
                        className="hp-book-cafe-policy-card__discount-offer"
                        data-testid="book-cafe-discount-price"
                      >
                        <span>1시간</span>{" "}
                        <strong className="hp-book-cafe-policy-card__discount-price">
                          {policy.hourlyPrice.toLocaleString("ko-KR")}원
                        </strong>
                      </span>
                      <small>할인 일정은 운영 상황에 따라 변경될 수 있습니다.</small>
                    </>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt>1회 예약</dt>
                <dd>최대 {maxContiguousHours}시간 연속</dd>
              </div>
            </dl>
          ) : null}
        </div>
      </section>

      <section className="hp-book-cafe-preflight" aria-labelledby="book-cafe-preflight-title">
        <h2 id="book-cafe-preflight-title">이용 전 확인</h2>
        <ul>
          <li><strong className="hp-book-cafe-guardian-notice">만 13세 이하 어린이는 보호자와 함께 이용해 주세요.</strong></li>
          <li><strong>반려동물 동반 금지</strong></li>
          <li>실내 조리와 휴대용 버너 등 화기 사용은 금지합니다.</li>
          <li>퇴실 전 정리하고, 시설 오염이나 파손이 있으면 관리실에 알려주세요.</li>
        </ul>
      </section>

      <section className="hp-book-cafe-guide__closing" aria-label="북카페 예약">
        <div className="hp-book-cafe-guide__actions">
          <Button
            className="hp-button hp-button--primary"
            variant="contained"
            onClick={moveToReservation}
          >
            북카페 예약하기
          </Button>
          <Button variant="outlined" onClick={() => navigate("/reservationbookcafeend")}>
            예약 내역
          </Button>
        </div>
      </section>
      <BookCafeManuals open={isManualsOpen} onClose={() => setIsManualsOpen(false)} />
    </main>
  );
};

export default BookCafeGuide;
